import { useBoardCamera } from '../components/useBoardCamera.js';
import { useEffect, useMemo, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import {
  ChessBoard,
  pieceThemeById,
  boardThemeById,
  tableThemeByMaterial,
  pieceMaterialById,
  pieceSizeById,
} from '@chessmate/board-3d';
import { ChessAdapter } from '@chessmate/game-engine';
import { START_FEN, type GameSnapshot } from '@chessmate/protocol';
import { Icon } from '@chessmate/ui';
import { Minimap } from '../components/Minimap.js';
import { Sidebar } from '../components/Sidebar.js';
import { SquareOverlay } from '../components/SquareOverlay.js';
import { useAssistHints } from '../components/useAssistHints.js';
import { useBoardReady } from '../components/useBoardReady.js';
import { OnlineGamePanel } from '../components/OnlineGamePanel.js';
import { useBackgammonStore } from '../state/backgammon-store.js';
import { useOnlineStore, type OnlineOpenInput } from '../state/online-store.js';
import { useThemeStore } from '../state/theme-store.js';
import { BackgammonScreen } from './BackgammonScreen.js';
import { useT } from '../i18n/useT.js';

const ONLINE_ADAPTER = new ChessAdapter();

export function OnlineGameScreen({ input, onExit }: { input: OnlineOpenInput; onExit: () => void }) {
  const boardCam = useBoardCamera();
  const { t } = useT('games');
  const game = useOnlineStore((s) => s.game);
  const phase = useOnlineStore((s) => s.phase);
  const selected = useOnlineStore((s) => s.selected);
  const targets = useOnlineStore((s) => s.targets);
  const lastMove = useOnlineStore((s) => s.lastMove);
  const error = useOnlineStore((s) => s.error);
  const moves = useOnlineStore((s) => s.moves);
  const pieceThemeId = useThemeStore((s) => s.pieceThemeId);
  const boardThemeId = useThemeStore((s) => s.boardThemeId);
  const tableMaterialId = useThemeStore((s) => s.tableMaterialId);
  const tableToneId = useThemeStore((s) => s.tableToneId);
  const pieceMaterialId = useThemeStore((s) => s.pieceMaterialId);
  const pieceSizeId = useThemeStore((s) => s.pieceSizeId);
  const pieceWhiteColor = useThemeStore((s) => s.pieceWhiteColor);
  const pieceBlackColor = useThemeStore((s) => s.pieceBlackColor);
  const showCoords = useThemeStore((s) => s.showCoords);
  const showMinimap = useThemeStore((s) => s.showMinimap);
  const submitPending = useOnlineStore((s) => s.submitPending);
  const [overlayTransform, setOverlayTransform] = useState<string>();
  const [boardReady, markBoardReady] = useBoardReady();
  const pos = useMemo(() => ONLINE_ADAPTER.fromFen(game?.fen ?? START_FEN), [game?.fen]);
  // Mirrors the guard in online-store `tap`: only an active game, on the
  // player's own turn, with no move in flight (and never as a spectator).
  const canMove =
    phase === 'playing' &&
    game?.status === 'active' &&
    game.spectating !== true &&
    game.turn === game.side &&
    !submitPending;
  const { movable, paths, unsafe, strongTargets } = useAssistHints(
    ONLINE_ADAPTER,
    pos,
    selected,
    targets,
    canMove,
  );

  useEffect(() => {
    void useOnlineStore.getState().open(input);
    return () => useOnlineStore.getState().close();
  }, [input]);

  // Online backgammon rides its own store (server-authoritative snapshot +
  // live:roll), not the chess adapter. Seeded once per session from the
  // opened game; the BackgammonScreen below renders from that store.
  const backgammonGame = useOnlineStore((s) => s.game)?.variant === 'backgammon'
    ? useOnlineStore((s) => s.game)
    : null;
  const bgSession = useBackgammonStore((s) => s.online);
  useEffect(() => {
    if (!backgammonGame || bgSession) return;
    useBackgammonStore.getState().openOnline({
      token: input.token,
      gameId: backgammonGame.id,
      side: backgammonGame.side,
      game: backgammonGame,
      spectating: backgammonGame.spectating === true,
    });
    return () => useBackgammonStore.getState().stop();
  }, [backgammonGame, bgSession, input]);

  // The opponent's last move drives the 3D arrow. Each MoveRecord carries
  // `by: 'w' | 'b'` and the player's side is `game.side`; the most recent
  // move by the *other* side is the opponent's last move. UCI encodes
  // from-to in the first four characters.
  const playerSide = game?.side;
  const lastOpponentMove = playerSide
    ? [...moves].reverse().find((m) => m.by !== playerSide)
    : undefined;
  const opponentArrow = lastOpponentMove
    ? {
        from: lastOpponentMove.uci.slice(0, 2),
        to: lastOpponentMove.uci.slice(2, 4),
      }
    : null;

  if (backgammonGame) {
    return <BackgammonScreen onExit={onExit} />;
  }

  return (
    <main className="mx-auto flex w-full max-w-5xl flex-col items-stretch gap-4 p-4 pt-4 lg:flex-row" data-testid="online-game-screen" data-appdom-scope="game">
      <Sidebar online>
        <OnlineGamePanel onExit={onExit} />
      </Sidebar>
      <section className="relative flex-1">
        <div
          className="relative aspect-square w-full rounded-3xl bg-board-frame max-sm:-mx-4 max-sm:w-[calc(100%+2rem)] max-sm:max-w-none max-sm:overflow-hidden max-sm:rounded-none shadow-xl"
          data-appdom-id="online-chess-board"
          data-appdom-type="Board"
        >
          <Canvas dpr={[1, 2]} camera={boardCam} onCreated={markBoardReady}>
            <ChessBoard
              fen={game?.fen ?? START_FEN}
              selected={selected}
              targets={targets}
              lastMove={lastMove}
              checkSquare={null}
              movable={movable}
              paths={paths}
              heatmap="off"
              pieceTheme={pieceThemeById(pieceThemeId)}
              boardTheme={boardThemeById(boardThemeId)}
              tableTheme={tableThemeByMaterial(tableMaterialId, tableToneId)}
              pieceMaterial={pieceMaterialById(pieceMaterialId)}
              pieceSize={pieceSizeById(pieceSizeId)}
              pieceWhiteColor={pieceWhiteColor}
              pieceBlackColor={pieceBlackColor}
              showCoords={showCoords}
              opponentArrow={opponentArrow}
              onOverlayTransform={setOverlayTransform}
            />
          </Canvas>
          <SquareOverlay
            selected={selected}
            targets={targets}
            movable={movable}
            paths={paths}
            unsafe={unsafe}
            strongTargets={strongTargets}
            transform={overlayTransform}
            onTap={(sq) => useOnlineStore.getState().tap(sq)}
            ready={boardReady}
            opponentArrow={opponentArrow}
          />
          {showMinimap && <Minimap fen={game?.fen ?? START_FEN} />}
          {phase === 'gameover' && (
            <div
              className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-3xl bg-ink/50 text-2xl font-extrabold text-cream"
              data-testid="online-game-over"
              data-appdom-id="online-game-over"
              data-appdom-type="Banner"
            >
              {game?.result && (game.result.kind === 'checkmate' || game.result.kind === 'resign') &&
                game.result.winner === game.side && (
                  <span className="inline-flex">
                    <Icon name="PartyPopper" size="lg" className="text-sun" />
                  </span>
                )}
              <span>{resultText(game, t)}</span>
            </div>
          )}
        </div>
        {error && (
          <p
            className="mt-2 font-bold text-berry"
            data-testid="online-error"
            data-appdom-id="online-error"
            data-appdom-type="Alert"
          >
            {error}
          </p>
        )}
      </section>
    </main>
  );
}

export function resultText(game: GameSnapshot | null, t: (key: string) => string): string {
  if (!game?.result) return t('game-over');
  const r = game.result;
  if (r.kind === 'checkmate' || r.kind === 'resign') {
    return r.winner === game.side ? t('you-win') : t('you-lose');
  }
  return t('it-is-a-draw');
}
